import type { DocumentOrigin, DocumentVersion, LibraryDocument } from '../../api/library'
import { contentUrl } from '../../api/library'
import { LinkButton } from '../../ui/Control'
import { asDate, asSize } from './Tiles'

/**
 * Where a document came from, and what it has been.
 *
 * Both halves answer the same question — *can I trust what I am looking at?* — and both are written
 * without naming a single external system: a route says its kind, and where that kind is "out of another
 * system" it says which one. ILIAS fills that in; nothing here knows about ILIAS.
 */

/**
 * The routes a document came by.
 *
 * More than one is ordinary: the same file uploaded by hand and later fetched from a university system
 * is one document that came both ways. A route that is synced says when it was last looked at, because
 * that is what makes "still current" a fact rather than a hope.
 */
export function Origins({ origins }: { readonly origins: readonly DocumentOrigin[] }) {
  /*
   * A document that was only ever written here says nothing worth reading by saying so: "Written in
   * MyLib", on a document somebody is looking at inside MyLib, is a line that answers a question
   * nobody asked. This list exists to answer *can I trust what I am looking at* — where it came
   * from, when it was last checked, whether it has since disappeared from there — and for something
   * written here there is no such answer to give.
   *
   * It reappears the moment there is more than one route: a document that arrived from ILIAS *and*
   * was written on here has something to say, and says both.
   */
  const worthSaying =
    origins.length > 1 ? origins : origins.filter((origin) => origin.kind !== 'authored')

  if (worthSaying.length === 0) {
    return null
  }

  return (
    <ul className="origins">
      {worthSaying.map((origin) => (
        <li className="origins__one" key={`${origin.kind}-${origin.source}`}>
          <span className="origins__what">{came(origin)}</span>
          {origin.location !== '' && (
            /*
             * Where it lay over there, said plainly and not as a link. The systems MyLib fetches
             * from do not all give an address that leads back — ILIAS gives no object id at all —
             * so this is the answer to "which of the four folders called Skripte was this", and
             * nothing more is claimed for it.
             */
            <span className="origins__where">{origin.location}</span>
          )}
          {origin.lastSyncedAt !== null && (
            <span className="origins__when">last checked {asDate(origin.lastSyncedAt)}</span>
          )}
          {origin.goneFromSourceAt !== null && (
            <span className="origins__gone">
              no longer in {origin.source} since {asDate(origin.goneFromSourceAt)}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

function came(origin: DocumentOrigin): string {
  switch (origin.kind) {
    case 'upload':
      return 'You added this file'
    case 'connected-system':
      return `Taken over from ${origin.source}`
    case 'photo':
      return 'Photographed or scanned here'
    case 'authored':
      return 'Written in MyLib'
    default:
      return 'Came into MyLib'
  }
}

/**
 * What the document has been, and the way back to each of them.
 *
 * A later sync of the same material does not replace what was there: it adds a version, and this is
 * where the earlier ones stay readable, each with the moment it arrived. Everything else in MyLib —
 * search, links, flashcards, a place on a board — refers to the *document*, and what it shows is
 * whichever version arrived last.
 */
export function Versions({
  document,
  versions,
}: {
  readonly document: LibraryDocument
  readonly versions: readonly DocumentVersion[]
}) {
  if (versions.length < 2) {
    return null
  }

  return (
    <section className="versions" aria-label="Versions">
      <h3 className="versions__title">This document has {versions.length} versions</h3>
      <ul className="versions__list">
        {versions.map((version) => (
          <li className="versions__one" key={version.id}>
            {/*
              Which line it belongs to, because the numbers repeat: the file that arrived and what
              somebody wrote here are counted apart, so "version 1" on its own would say nothing. It
              is said in words rather than as `own`/`source` — "you wrote this" is what somebody
              means by it.
            */}
            <span className="versions__which">
              {version.current
                ? 'Now'
                : version.line === 'own'
                  ? `Your version ${version.ordinal}`
                  : `As it arrived, ${version.ordinal}`}
            </span>
            <span className="versions__facts">
              <span>{asDate(version.takenOverAt)}</span>
              <span aria-hidden="true">·</span>
              <span>{asSize(version.sizeInBytes)}</span>
            </span>
            <LinkButton
              tone="bare"
              href={contentUrl(document, { download: true, version: version.id })}
              download={version.fileName}
              rel="noreferrer"
            >
              Download
            </LinkButton>
          </li>
        ))}
      </ul>
    </section>
  )
}
